import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { ChartContainer } from '../components/ui/ChartContainer';
import { FaArrowLeft, FaChartLine, FaDownload } from 'react-icons/fa';
import { getTerminalName } from '../utils/terminalNames';
import { daysAgoDateInputCL, toDateInputValueCL } from '../config/regional';

const MetricCard = ({ label, value, unit }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{label}</p>
    <p className="text-lg font-bold text-gray-900 mt-1">
      {value !== null && value !== undefined ? `${Number(value).toFixed(1)}${unit}` : 'N/A'}
    </p>
  </div>
);

const LegendCheck = ({ checked, onChange, label, color }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="inline-block w-3.5 h-3.5 rounded-sm border border-gray-300" style={{ backgroundColor: color }} />
    <span>{label}</span>
  </label>
);

const GraphPage = ({ token }) => {
  const { guid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => daysAgoDateInputCL(6));
  const [endDate, setEndDate] = useState(() => toDateInputValueCL(new Date()));
  const [tempSeries, setTempSeries] = useState({ air: true, d30: true, d60: true });
  const [humSeries, setHumSeries] = useState({ air: true, d30: true, d60: true });
  const [condSeries, setCondSeries] = useState({ d30: true, d60: true });
  const terminalName = getTerminalName({ guid });

  useEffect(() => {
    const fetchData = async () => {
      if (!guid || !token) return;
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/lecturas/getLecturasByGuid/${guid}?startDate=${startDate}&endDate=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });

        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        const processedData = rows.map(item => {
          const horaCompleta = item.hora ? item.hora.split('.')[0] : '';
          const fechaHora = `${item.fecha} ${horaCompleta}`.trim();
          return ({
          ...item,
          fecha_hora: fechaHora,
          hora: horaCompleta,
          temperature_air: parseFloat(item.temperature_air),
          humidity_air: parseFloat(item.humidity_air),
          temperature_soil_30cm: parseFloat(item.temperature_soil_30cm),
          humidity_soil_30cm: parseFloat(item.humidity_soil_30cm),
          conductivity_soil_30cm: parseFloat(item.conductivity_soil_30cm),
          temperature_soil_60cm: parseFloat(item.temperature_soil_60cm),
          humidity_soil_60cm: parseFloat(item.humidity_soil_60cm),
          conductivity_soil_60cm: parseFloat(item.conductivity_soil_60cm),
        });
        });
        setData(processedData);
      } catch (error) {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [guid, token, startDate, endDate]);

  const lastReading = data[data.length - 1] || {};

  const renderMultiChart = (title, series, unit, controls) => (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <CardTitle className="flex items-center gap-2">
            <FaChartLine className="w-4 h-4 text-emerald-500" />
            {title}
          </CardTitle>
          <div className="flex flex-wrap gap-3 text-sm">
            {controls}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="fecha_hora"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
              minTickGap={24}
              tickFormatter={(value) => (typeof value === 'string' ? value.slice(11, 16) : value)}
            />
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <Tooltip
              formatter={(value, name) => [`${Number(value).toFixed(1)} ${unit}`, name]}
              contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
            />
            {series.map((s) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  const downloadCsv = () => {
    const tempKeys = [];
    const humKeys = [];
    const condKeys = [];
    if (tempSeries.air) tempKeys.push('temperature_air');
    if (tempSeries.d30) tempKeys.push('temperature_soil_30cm');
    if (tempSeries.d60) tempKeys.push('temperature_soil_60cm');
    if (humSeries.air) humKeys.push('humidity_air');
    if (humSeries.d30) humKeys.push('humidity_soil_30cm');
    if (humSeries.d60) humKeys.push('humidity_soil_60cm');
    if (condSeries.d30) condKeys.push('conductivity_soil_30cm');
    if (condSeries.d60) condKeys.push('conductivity_soil_60cm');

    const cols = ['fecha', 'hora', ...tempKeys, ...humKeys, ...condKeys];
    const csvRows = [cols.join(',')];
    data.forEach((r) => {
      csvRows.push(cols.map((c) => r[c] ?? '').join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = terminalName.replace(/[^a-zA-Z0-9-_]/g, '_');
    link.setAttribute('download', `${safeName}_${startDate}_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate('/terminales')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
        <FaArrowLeft className="w-4 h-4" />
        Volver a Terminales
      </button>

      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{terminalName}</h1>
            <p className="text-xs text-gray-500 font-mono mt-1">GUID: {guid}</p>
            <p className="text-sm text-gray-500 mt-1">{data.length} lecturas entre {startDate} y {endDate}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
            <button onClick={downloadCsv} className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm">
              <FaDownload className="w-3.5 h-3.5" /> Descargar
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Temp. Aire" value={lastReading.temperature_air} unit="°C" />
        <MetricCard label="Humedad Aire" value={lastReading.humidity_air} unit="%" />
        <MetricCard label="Temp. Suelo 30cm" value={lastReading.temperature_soil_30cm} unit="°C" />
        <MetricCard label="Humedad Suelo 30cm" value={lastReading.humidity_soil_30cm} unit="%" />
        <MetricCard label="Temp. Suelo 60cm" value={lastReading.temperature_soil_60cm} unit="°C" />
        <MetricCard label="Humedad Suelo 60cm" value={lastReading.humidity_soil_60cm} unit="%" />
        <MetricCard label="Conductividad 30cm" value={lastReading.conductivity_soil_30cm} unit="µS/cm" />
        <MetricCard label="Conductividad 60cm" value={lastReading.conductivity_soil_60cm} unit="µS/cm" />
      </div>

      <ChartContainer>
        {renderMultiChart('Temperatura', [
          ...(tempSeries.air ? [{ key: 'temperature_air', label: 'Ambiente', color: '#34d399' }] : []),
          ...(tempSeries.d30 ? [{ key: 'temperature_soil_30cm', label: 'Suelo 30cm', color: '#fbbf24' }] : []),
          ...(tempSeries.d60 ? [{ key: 'temperature_soil_60cm', label: 'Suelo 60cm', color: '#60a5fa' }] : []),
        ], '°C', (
          <>
            <LegendCheck checked={tempSeries.air} onChange={(e) => setTempSeries({ ...tempSeries, air: e.target.checked })} label="Ambiente" color="#34d399" />
            <LegendCheck checked={tempSeries.d30} onChange={(e) => setTempSeries({ ...tempSeries, d30: e.target.checked })} label="30cm" color="#fbbf24" />
            <LegendCheck checked={tempSeries.d60} onChange={(e) => setTempSeries({ ...tempSeries, d60: e.target.checked })} label="60cm" color="#60a5fa" />
          </>
        ))}

        {renderMultiChart('Humedad', [
          ...(humSeries.air ? [{ key: 'humidity_air', label: 'Ambiente', color: '#22d3ee' }] : []),
          ...(humSeries.d30 ? [{ key: 'humidity_soil_30cm', label: 'Suelo 30cm', color: '#818cf8' }] : []),
          ...(humSeries.d60 ? [{ key: 'humidity_soil_60cm', label: 'Suelo 60cm', color: '#f472b6' }] : []),
        ], '%', (
          <>
            <LegendCheck checked={humSeries.air} onChange={(e) => setHumSeries({ ...humSeries, air: e.target.checked })} label="Ambiente" color="#22d3ee" />
            <LegendCheck checked={humSeries.d30} onChange={(e) => setHumSeries({ ...humSeries, d30: e.target.checked })} label="30cm" color="#818cf8" />
            <LegendCheck checked={humSeries.d60} onChange={(e) => setHumSeries({ ...humSeries, d60: e.target.checked })} label="60cm" color="#f472b6" />
          </>
        ))}

        {renderMultiChart('Conductividad', [
          ...(condSeries.d30 ? [{ key: 'conductivity_soil_30cm', label: 'Suelo 30cm', color: '#fb7185' }] : []),
          ...(condSeries.d60 ? [{ key: 'conductivity_soil_60cm', label: 'Suelo 60cm', color: '#fdba74' }] : []),
        ], '', (
          <>
            <LegendCheck checked={condSeries.d30} onChange={(e) => setCondSeries({ ...condSeries, d30: e.target.checked })} label="30cm" color="#fb7185" />
            <LegendCheck checked={condSeries.d60} onChange={(e) => setCondSeries({ ...condSeries, d60: e.target.checked })} label="60cm" color="#fdba74" />
          </>
        ))}
      </ChartContainer>
    </div>
  );
};

export default GraphPage;
